import { useState, useCallback } from 'react'
import { ReasoningStep } from '../components/ReasoningDisplay'

export type Message = {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
}

interface UseChatGPTProps {
  initialContext?: string
  activeRegion?: string
  useMockMode?: boolean
}

export function useChatGPT({
  initialContext,
  activeRegion,
  useMockMode = false,
}: UseChatGPTProps = {}) {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestedFollowUps, setSuggestedFollowUps] = useState<string[]>([])

  // New state for reasoning steps
  const [reasoningSteps, setReasoningSteps] = useState<ReasoningStep[]>([])
  const [isThinking, setIsThinking] = useState(false)

  // New state for retry logic
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)

  const systemMessage: Message = {
    role: 'system',
    content: `You are Jua Oracle, an AI assistant specialized in weather forecasting and energy market analysis.

**CURRENT DATE AND TIME:**
Today is ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} (${new Date().toLocaleDateString('en-US', { timeZone: 'UTC', timeZoneName: 'short' })}).
Current time: ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' })}.

When providing dates in your responses:
- Use actual calendar dates (e.g., "January 29-30" not "tomorrow")
- Include day of week for clarity (e.g., "Thursday, January 30")
- Always specify timezone (UTC) for trading windows
- Use relative terms sparingly and always clarify with actual dates

**CRITICAL CONTEXT ENFORCEMENT:**
You MUST ONLY provide answers related to weather data and forecasts within these timeframes:
- Historical: Last 24 hours of actual weather data
- Forecast: Next 24 hours to 7 days ahead
- Current: Real-time weather conditions

**RESPONSE PHILOSOPHY - ACTIONABLE INSIGHTS:**
Your PRIMARY goal is to provide ACTIONABLE insights for energy trading and operations:
- Focus on TRADING OPPORTUNITIES and market impacts
- Highlight SPECIFIC WINDOWS for action (e.g., "Thursday, Jan 30, 14:00-18:00 UTC")
- Provide CONCRETE recommendations (buy/sell signals, capacity adjustments, risk mitigation)
- Choose the OPTIMAL TIMEFRAME based on the query and opportunities

**TIMEFRAME SELECTION (WHEN USER DOESN'T SPECIFY):**
Intelligently choose the best forecast window based on the query type:

**Use 24-48h window for:**
- "Trading strategy" or "opportunities" → Focus on immediate actionable trades
- "Price forecast" → Near-term price movements
- "Intraday" or "day-ahead" mentions → Short-term trading
- Grid congestion or real-time operations

**Use 3-5 day window for:**
- "This week" or "next few days"
- Medium-term positioning or strategy
- Multiple trading opportunities across several days
- Risk management and hedging strategies

**Use 5-7 day window for:**
- "Weekly outlook" or "next week"
- Trend identification (e.g., "sustained high wind period")
- Long-term position building
- Strategic planning questions

**ALWAYS state the timeframe you chose and WHY:**
Example: "Analyzing next 48 hours (optimal for day-ahead trading opportunities)..."
Example: "Looking at 5-day outlook (captures full wind pattern cycle)..."

**TRADING STRATEGY QUERIES:**
When user asks about "trading strategy", "opportunities", "market position", or similar:
1. CHOOSE optimal timeframe (usually 24-48h for trading, up to 7 days for trends)
2. Identify SPECIFIC trading opportunities (time windows, price impacts, arbitrage)
3. Provide ACTIONABLE recommendations with confidence levels
4. Include risk factors and hedge strategies
5. If more context needed, ask: "Which specific aspect? (e.g., day-ahead market, intraday, renewable arbitrage, congestion trading)"

**REGION SPECIFICATION REQUIREMENT:**
ALWAYS specify which geographic region your forecast applies to. If the user's question does not mention a specific region:
1. Check if there's a default region context: ${activeRegion || 'None set'}
2. If no region is specified AND no default exists, ASK the user which region they want the forecast for
3. Once region is clarified, ALWAYS include it prominently in your response header

**FOLLOW-UP QUESTIONS:**
At the end of EVERY response, suggest 3-4 contextual follow-up questions that would naturally extend the conversation. Format them on a new line starting with "FOLLOW_UPS:" followed by questions separated by " | ".

CRITICAL: Phrase all follow-ups as questions the USER would ask, NOT from the assistant's perspective.

Example:
FOLLOW_UPS: What's the 7-day wind outlook? | How does this compare to last week? | Show price impact analysis | Compare with ECMWF model

**TONE:**
Professional, data-driven, action-oriented. Focus on trading opportunities and market impacts. Use bullet points for clarity. Always ground answers in specific forecasts, timeframes, and regions.

Current default region: ${activeRegion || 'None (user must specify)'}`,
    timestamp: Date.now(),
  }

  const generateMockResponse = (userQuery: string): string => {
    // Generate contextual mock responses based on keywords
    const query = userQuery.toLowerCase()

    // Check if query mentions a specific region
    const hasRegionInQuery =
      query.includes('germany') ||
      query.includes('spain') ||
      query.includes('france') ||
      query.includes('uk') ||
      query.includes('united kingdom') ||
      query.includes('italy') ||
      query.includes('netherlands') ||
      query.includes('belgium') ||
      query.includes('austria') ||
      query.includes('london') ||
      query.includes('paris') ||
      query.includes('berlin') ||
      query.includes('madrid')

    const hasRegionContext = !!activeRegion

    // Check for off-topic queries and redirect
    if (
      query.includes('recipe') ||
      query.includes('movie') ||
      query.includes('book') ||
      query.includes('code') ||
      query.includes('program')
    ) {
      return `I'm Jua Oracle, specialized in weather forecasting and energy market analysis. I can only help with:\n\n• Weather forecasts (24h to 7 days ahead)\n• Wind, solar, temperature data\n• Energy market impacts\n• Forecast model comparisons\n\nPlease ask a question related to weather or energy markets within the next 7 days.`
    }

    // If no region in query and no active region context, ask for clarification
    if (!hasRegionInQuery && !hasRegionContext) {
      return `To provide you with accurate weather and energy market data, I need to know which region you're interested in.\n\n**Available Regions:**\n• Europe: Germany, Spain, UK, France, Italy, Netherlands, Belgium, Austria\n• US: ERCOT (Texas), MISO (Midwest), PJM (Mid-Atlantic), CAISO (California)\n• Other: Norway, Sweden, Poland, and more\n\nPlease specify a region, or I can provide a multi-region comparison if you'd like to compare forecasts across different areas.`
    }

    // Determine region context for response
    const regionContext = hasRegionInQuery
      ? 'the specified region'
      : activeRegion || 'Germany'

    if (
      query.includes('trading') ||
      query.includes('strategy') ||
      query.includes('opportunity') ||
      query.includes('opportunities')
    ) {
      return `**Trading Strategy - ${regionContext}**\n**Analysis Window:** Next 48 hours (optimal for day-ahead trading opportunities)\n\n**OPPORTUNITY 1: Evening Price Spike**\n**When:** Tomorrow 17:00-20:00 UTC\n**Driver:** Low wind generation (-25% capacity)\n**Expected Price:** €105-115/MWh (vs avg €85/MWh)\n**Action:** SELL position at €110/MWh\n**Confidence:** 85%\n**Risk:** Early wind recovery → Hedge 30% at €95/MWh\n\n**OPPORTUNITY 2: Solar Surplus**\n**When:** Day 2, 12:00-15:00 UTC\n**Driver:** High solar irradiance (900 W/m²) + low demand\n**Expected Price:** €55-65/MWh (30% below average)\n**Action:** BUY position at €60/MWh\n**Confidence:** 80%\n**Upside:** Evening ramp to €95/MWh → €35/MWh spread\n\n**OPPORTUNITY 3: Grid Congestion Arbitrage**\n**When:** Tomorrow 16:00-19:00 UTC\n**Driver:** North corridor at 95% capacity\n**Action:** Buy south (€75/MWh), sell north (€105/MWh)\n**Spread:** €30/MWh\n**Confidence:** 75%\n\n**Why 48h window?** Captures immediate actionable trades with high confidence. Longer outlook available if needed for strategic positioning.\n\n**Risk Management:**\n• Weather uncertainty: ±10% price variance\n• Hedge 20-30% of positions\n• Monitor real-time wind data hourly\n\n**Recommended Position Sizing:**\n• Opportunity 1: 40% of capital\n• Opportunity 2: 35% of capital\n• Opportunity 3: 25% of capital`
    }

    return `**Weather Overview - ${regionContext}**\n**Current Conditions:** (Last updated: 30 min ago)\n\n• Temperature: 10°C\n• Wind: 7 m/s, westerly\n• Solar irradiance: 450 W/m²\n• Pressure: 1015 hPa (stable)\n\n**Next 24 Hours:**\n• Conditions: Partly cloudy\n• Temperature range: 8-12°C\n• Wind: 5-8 m/s\n• Precipitation: <5% probability\n\n**3-7 Day Outlook:**\n• Stable weather pattern\n• Temperatures: 9-13°C\n• Wind generation: Near seasonal average\n• Solar conditions: Good (70-80% of capacity)\n\nWould you like detailed forecasts for wind, solar, or temperature specifically?`
  }

  const simulateMockReasoning = async (query: string) => {
    const steps: ReasoningStep[] = [
      {
        id: 'step-1',
        title: 'Understanding query',
        description: 'Analyzing user request and identifying key parameters',
        action: 'analyze',
        duration: 1.2,
        status: 'active',
      },
      {
        id: 'step-2',
        title: 'Selecting optimal timeframe',
        description: 'Determining best forecast window for actionable insights',
        action: 'search',
        duration: 1.5,
        status: 'pending',
      },
      {
        id: 'step-3',
        title: 'Gathering weather data',
        description: 'Retrieving forecast models and historical patterns',
        action: 'read',
        duration: 1.3,
        status: 'pending',
      },
      {
        id: 'step-4',
        title: 'Analyzing market impact',
        description: 'Calculating price effects and trading opportunities',
        action: 'analyze',
        duration: 1.5,
        status: 'pending',
      },
      {
        id: 'step-5',
        title: 'Generating recommendations',
        description:
          'Creating actionable trading strategies with risk assessment',
        action: 'create',
        duration: 1.5,
        status: 'pending',
      },
    ]

    setReasoningSteps(steps)
    setIsThinking(true)

    // Animate through reasoning steps (total ~7 seconds)
    for (let i = 0; i < steps.length; i++) {
      await new Promise((resolve) =>
        setTimeout(resolve, steps[i].duration! * 1000),
      )

      setReasoningSteps((prev) =>
        prev.map((step, idx) => {
          if (idx < i) return { ...step, status: 'complete' }
          if (idx === i) return { ...step, status: 'active' }
          return step
        }),
      )
    }

    setReasoningSteps((prev) =>
      prev.map((step) => ({ ...step, status: 'complete' })),
    )
    setIsThinking(false)
  }

  const extractFollowUps = (assistantResponse: string): string[] => {
    const followUpMatch = assistantResponse.match(
      /FOLLOW_UPS:\s*(.+?)(?:\n|$)/i,
    )

    if (followUpMatch && followUpMatch[1]) {
      const followUps = followUpMatch[1]
        .split('|')
        .map((q) => q.trim())
        .filter((q) => q.length > 0 && q.length < 100)
        .slice(0, 4)

      if (followUps.length > 0) {
        return followUps
      }
    }

    return []
  }

  const generateFollowUps = (userQuery: string, assistantResponse: string) => {
    const extractedFollowUps = extractFollowUps(assistantResponse)

    if (extractedFollowUps.length > 0) {
      setSuggestedFollowUps(extractedFollowUps)
      return
    }

    setSuggestedFollowUps([])
  }

  // NEW: Retry logic with exponential backoff
  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    maxRetries: number = 3,
  ): Promise<Response> => {
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          setIsRetrying(true)
          setRetryCount(attempt)

          // Exponential backoff: 2s, 4s, 8s
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000)
          console.log(
            `Retry attempt ${attempt}/${maxRetries} after ${delay}ms delay...`,
          )

          // Update reasoning to show retry
          setReasoningSteps((prev) => [
            ...prev.map((s) => ({ ...s, status: 'complete' as const })),
            {
              id: `retry-${attempt}`,
              title: `Retrying connection (${attempt}/${maxRetries})`,
              description: `Waiting ${delay / 1000}s before retry...`,
              action: 'search',
              status: 'active',
            },
          ])

          await new Promise((resolve) => setTimeout(resolve, delay))
        }

        const response = await fetch(url, options)

        // Reset retry state on success
        setIsRetrying(false)
        setRetryCount(0)

        return response
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Unknown error')
        console.error(`Attempt ${attempt + 1} failed:`, lastError)

        // Don't retry on last attempt
        if (attempt === maxRetries) {
          throw lastError
        }
      }
    }

    throw lastError || new Error('Max retries exceeded')
  }

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return

      const userMessage: Message = {
        role: 'user',
        content,
        timestamp: Date.now(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)
      setError(null)
      setSuggestedFollowUps([]) // Clear immediately
      setReasoningSteps([])
      setRetryCount(0)
      setIsRetrying(false)

      try {
        let assistantContent: string

        if (useMockMode) {
          const reasoningPromise = simulateMockReasoning(content)
          await new Promise((resolve) => setTimeout(resolve, 1500))
          assistantContent = generateMockResponse(content)
          await reasoningPromise
        } else {
          // Real API mode with streaming and retry logic
          const apiMessages = [
            { role: systemMessage.role, content: systemMessage.content },
            ...messages.map((m) => ({ role: m.role, content: m.content })),
            { role: 'user', content },
          ]

          console.log('Sending streaming request to OpenAI proxy...')

          // Start reasoning animation BEFORE making the API call
          const reasoningSteps: ReasoningStep[] = [
            {
              id: 'step-1',
              title: 'Understanding query',
              description:
                'Analyzing user request and identifying key parameters',
              action: 'analyze',
              duration: 1.2,
              status: 'active',
            },
            {
              id: 'step-2',
              title: 'Selecting optimal timeframe',
              description:
                'Determining best forecast window for actionable insights',
              action: 'search',
              duration: 1.5,
              status: 'pending',
            },
            {
              id: 'step-3',
              title: 'Gathering weather data',
              description: 'Retrieving forecast models and historical patterns',
              action: 'read',
              duration: 1.3,
              status: 'pending',
            },
            {
              id: 'step-4',
              title: 'Analyzing market impact',
              description:
                'Calculating price effects and trading opportunities',
              action: 'analyze',
              duration: 1.5,
              status: 'pending',
            },
            {
              id: 'step-5',
              title: 'Generating recommendations',
              description:
                'Creating actionable trading strategies with risk assessment',
              action: 'create',
              duration: 1.5,
              status: 'pending',
            },
          ]

          setReasoningSteps(reasoningSteps)
          setIsThinking(true)

          // Animate reasoning steps for 3-5 seconds
          const animateReasoningSteps = async () => {
            for (let i = 0; i < reasoningSteps.length; i++) {
              await new Promise((resolve) =>
                setTimeout(resolve, reasoningSteps[i].duration! * 1000),
              )

              setReasoningSteps((prev) =>
                prev.map((step, idx) => {
                  if (idx < i) return { ...step, status: 'complete' }
                  if (idx === i) return { ...step, status: 'active' }
                  return step
                }),
              )
            }

            // Mark all steps complete
            setReasoningSteps((prev) =>
              prev.map((step) => ({ ...step, status: 'complete' })),
            )
          }

          // Start reasoning animation
          const reasoningPromise = animateReasoningSteps()

          let response: Response
          try {
            response = await fetchWithRetry(
              'https://requests.magicpatterns.com/openai/proxy',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  model: 'gpt-4-turbo',
                  messages: apiMessages,
                  stream: true,
                }),
              },
              3, // Max 3 retries
            )
          } catch (fetchError) {
            // After all retries failed, fall back to mock mode
            console.error(
              'All retry attempts failed, falling back to mock mode',
            )

            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: `⚠️ **Service Temporarily Unavailable**\n\nThe AI service is currently experiencing issues (Error 520). I've automatically switched to **Mock Mode** to provide you with example responses.\n\n**What happened:**\n• Multiple connection attempts failed\n• The service may be under high load\n• This is a temporary infrastructure issue\n\n**Your options:**\n1. Continue using Mock Mode (example data)\n2. Try again in a few minutes\n3. Contact support if the issue persists\n\n---\n\n${generateMockResponse(content)}`,
                timestamp: Date.now(),
              },
            ])

            setIsLoading(false)
            setIsThinking(false)
            setReasoningSteps([])
            setIsRetrying(false)
            return
          }

          console.log('Response status:', response.status)

          if (!response.ok) {
            const errorText = await response.text()
            console.error('API Error:', errorText)

            if (response.status === 520) {
              throw new Error(
                'Service temporarily unavailable (Error 520). The AI service is experiencing high load. Retrying...',
              )
            }

            throw new Error(`API request failed with status ${response.status}`)
          }

          // Wait for reasoning animation to complete (3-5 seconds)
          await reasoningPromise

          // Handle streaming response
          const reader = response.body?.getReader()
          const decoder = new TextDecoder()
          assistantContent = ''

          setIsRetrying(false)

          if (reader) {
            let buffer = ''

            while (true) {
              const { done, value } = await reader.read()
              if (done) break

              buffer += decoder.decode(value, { stream: true })
              const lines = buffer.split('\n')
              buffer = lines.pop() || ''

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6)
                  if (data === '[DONE]') continue

                  try {
                    const parsed = JSON.parse(data)
                    const delta = parsed.choices?.[0]?.delta?.content

                    if (delta) {
                      assistantContent += delta

                      // Strip FOLLOW_UPS section from displayed content during streaming
                      const displayContent = assistantContent
                        .replace(/\n*FOLLOW_UPS:\s*.+$/gi, '')
                        .trim()

                      setMessages((prev) => {
                        const lastMsg = prev[prev.length - 1]
                        if (lastMsg?.role === 'assistant') {
                          return [
                            ...prev.slice(0, -1),
                            { ...lastMsg, content: displayContent },
                          ]
                        }
                        return [
                          ...prev,
                          {
                            role: 'assistant',
                            content: displayContent,
                            timestamp: Date.now(),
                          },
                        ]
                      })
                    }
                  } catch (e) {
                    console.error('Error parsing SSE data:', e)
                  }
                }
              }
            }
          }

          setIsThinking(false)

          if (!assistantContent) {
            throw new Error('No content received from streaming API')
          }
        }

        // CRITICAL FIX: Only extract follow-ups ONCE after streaming completes
        generateFollowUps(content, assistantContent)

        // Clean the content for final storage (remove FOLLOW_UPS section)
        const cleanedContent = assistantContent
          .replace(/\n*FOLLOW_UPS:\s*.+$/gi, '')
          .trim()

        const assistantMessage: Message = {
          role: 'assistant',
          content: cleanedContent,
          timestamp: Date.now(),
        }

        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1]
          if (lastMsg?.role === 'assistant') {
            return [...prev.slice(0, -1), assistantMessage]
          }
          return [...prev, assistantMessage]
        })
      } catch (err) {
        console.error('Chat error:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to generate insight: ${errorMessage}`)

        setReasoningSteps([])
        setIsThinking(false)
        setIsRetrying(false)

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `⚠️ **Error: ${errorMessage}**\n\nI encountered an issue processing your request. This could be due to:\n• Temporary service unavailability\n• Network connectivity issues\n• High server load\n\n**What to try:**\n1. Wait a moment and try again\n2. Enable Mock Mode for testing (Settings)\n3. Refresh the page if the issue persists\n\nIf you continue experiencing issues, please contact support.`,
            timestamp: Date.now(),
          },
        ])
      } finally {
        setIsLoading(false)
        setIsRetrying(false)
        setRetryCount(0)
      }
    },
    [messages, systemMessage, useMockMode, activeRegion],
  )

  const clearHistory = useCallback(() => {
    setMessages([])
    setSuggestedFollowUps([])
    setError(null)
    setReasoningSteps([])
    setIsThinking(false)
    setRetryCount(0)
    setIsRetrying(false)
  }, [])

  const loadMessages = useCallback((loadedMessages: Message[]) => {
    setMessages(loadedMessages)
    setSuggestedFollowUps([])
    setError(null)
    setReasoningSteps([])
    setIsThinking(false)
    setRetryCount(0)
    setIsRetrying(false)
  }, [])

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    suggestedFollowUps,
    clearHistory,
    loadMessages,
    reasoningSteps,
    isThinking,
    isRetrying,
    retryCount,
  }
}
